// Front rule shape (built up by components/rules/**) → backend RuleDto shape
// (src/api/rule/dtos/base-rule.dto.ts, Prime-MOQ-Backend). Much simpler than
// adaptors/campaign/save.adaptor.js since Rule has no conflict-detection or
// per-discount-type branching to reshape around.

// The eligibility pickers (components/rules/eligibility/*PickerField.js) were
// each adapted from their campaignV4 counterpart and still build Discounty's
// row shape: { id, isActive, isExclude, name, isDeleted }. MOQ's tables are not
// those tables:
//
//   RuleCustomer         shopifyId (required), firstName?, lastName?, meta?
//   RuleCustomerTag      tagName (required)
//   RuleCustomerSegment  shopifyId (required), name (required)
//   RuleCountry          countryCode (required)
//
// None of them has isActive/isExclude/isDeleted, and every one of them needs a
// field the picker keeps in `id`. These four were previously passed through a
// `stripId` helper that deleted `id` — the ONLY identifying field here, since
// these pickers put the SHOPIFY identifier there — and posted three columns
// that do not exist. That is why `prisma.rule.create` failed with "Argument
// `shopifyId` is missing", and why RuleCustomer, RuleCustomerTag and
// RuleCustomerSegment all still had zero rows: no customer-targeted rule had
// ever saved successfully, on any install.
//
// `stripId` was removed with them; the scope relations have their own mappers
// below and never used it.
const mapCustomers = (rows) =>
  (rows ?? []).map((c) => ({
    shopifyId: String(c.shopifyId ?? c.id ?? ''),
    firstName: c.firstName ?? undefined,
    lastName: c.lastName ?? undefined,
    meta: c.meta ?? undefined,
  }));

// The tag picker sets both `id` and `name` to the tag string itself.
const mapCustomerTags = (rows) =>
  (rows ?? []).map((t) => ({ tagName: String(t.tagName ?? t.name ?? t.id ?? '') }));

const mapCustomerSegments = (rows) =>
  (rows ?? []).map((s) => ({
    shopifyId: String(s.shopifyId ?? s.id ?? ''),
    name: String(s.name ?? ''),
  }));

const mapCountries = (rows) =>
  (rows ?? []).map((c) => ({ countryCode: String(c.countryCode ?? c.code ?? c.id ?? '') }));

// rule.scopeProducts is one row PER PRODUCT (utils/mapData.js's
// mapProductstoSave groups App Bridge's picked variants under a single
// {productId, variantIdList, variants, ...} row) — the backend's RuleProductDto
// is one row per (productId, variantId) pair (matches the RuleProduct Prisma
// model), so this has to flatten each product's variantIdList/variants back
// out into individual rows, not pass a nonexistent `p.variantId` through.
// Each variant's own title (e.g. "XL") is stashed in `meta.variantTitle` —
// meta is free-form JSON on the backend, so no schema change is needed —
// otherwise it's lost on save and reappears as a repeat of the product title
// on reload (see get.adaptor.js's fromProducts).
function mapProducts(scopeProducts) {
  return (scopeProducts ?? []).flatMap((p) => {
    const variantsById = new Map((p.variants ?? []).map((v) => [String(v?.id ?? '').split('/').pop(), v]));
    const idsFromList = (p.variantIdList ?? []).filter(Boolean);
    const variantIds = [...new Set([...idsFromList, ...variantsById.keys()])].filter(Boolean);

    return variantIds.map((variantId) => {
      const variantEntry = variantsById.get(variantId);
      const variantTitle = variantEntry?.title;
      return {
        productId: String(p.productId),
        variantId: String(variantId),
        meta: {
          title: p.title,
          handle: p.handle,
          images: p.image?.src ? [{ originalSrc: p.image.src }] : [],
          variantTitle: variantTitle && variantTitle !== 'Default Title' ? variantTitle : '',
        },
        isExcluded: Boolean(p.isExclude),
        // Rule precedence (see get.adaptor.js's fromProducts) — must survive
        // a normal editor save, or the merchant loses the "excluded because
        // of another rule" marker the moment they touch any other field.
        isAutomatic: Boolean(variantEntry?.isAutomatic),
        conflictedRuleId: variantEntry?.conflictedRuleId ?? undefined,
      };
    });
  });
}

function mapCollections(scopeCollections) {
  return (scopeCollections ?? []).map((c) => ({
    collectionId: String(c.collectionId),
    meta: { title: c.collection?.title ?? c.title },
    isExcluded: Boolean(c.isExclude),
  }));
}

function mapTags(scopeTags) {
  return (scopeTags ?? []).map((t) => ({ tagName: t.tagName, isExcluded: Boolean(t.isExclude) }));
}

function mapVendors(scopeVendors) {
  // Historical quirk carried over from the campaign scope pickers: the
  // vendor *name* is stored under the `vendorId` key on the front object.
  return (scopeVendors ?? []).map((v) => ({ vendorName: v.vendorId, isExcluded: Boolean(v.isExclude) }));
}

function mapProductTypes(scopeProductTypes) {
  return (scopeProductTypes ?? []).map((t) => ({
    productTypeName: t.productTypeName,
    isExcluded: Boolean(t.isExclude),
  }));
}

function mapPriceRanges(scopePriceRanges) {
  return (scopePriceRanges ?? []).map((r) => ({
    fromPrice: Number(r.fromPrice) || 0,
    toPrice: Number(r.toPrice) || 0,
    isExcluded: Boolean(r.isExclude),
  }));
}

function mapVariantMetafieldConditions(scopeVariantMetafieldConditions) {
  return (scopeVariantMetafieldConditions ?? []).map((c) => ({
    namespace: c.namespace,
    key: c.key,
    operator: c.operator,
    value: c.value,
    isExcluded: Boolean(c.isExclude),
  }));
}

export function saveRuleAdaptor(rule) {
  return {
    id: rule.id,
    shopId: rule.shopId,
    name: rule.name,
    type: rule.type,
    status: rule.status,

    min: rule.min || undefined,
    max: rule.max || undefined,
    multiple: rule.multiple || undefined,
    minValue: rule.minValue || undefined,
    maxValue: rule.maxValue || undefined,
    minWeight: rule.minWeight || undefined,
    maxWeight: rule.maxWeight || undefined,
    weightUnit: rule.weightUnit,
    repurchasePeriod: rule.repurchasePeriod,
    applyMode: rule.applyMode ?? 'each_item',
    countVariantsSeparately: rule.countVariantsSeparately ?? true,

    isImmediately: rule.isImmediately ?? true,
    startAt: rule.startDate ?? null,
    endAt: rule.endDate ?? null,

    scopeType: rule.scopeType,
    hasExcludes: Boolean(rule.hasExcludes),
    scopeExcludeType: rule.hasExcludes ? rule.scopeExcludeType : undefined,
    includeOnFly: Boolean(rule.includeOnFly),
    excludeOnFly: Boolean(rule.excludeOnFly),

    customerSelectionType: rule.customerSelectionType ?? 'all',

    contactEnabled: Boolean(rule.contact ?? rule.contactEnabled),
    contactUrl: rule.contactUrl,
    showHint: rule.showHint ?? true,

    messages: rule.messages,
    design: rule.design,

    // Pro feature A (docs/shopify-pro-features-spec.md §A) — per-bound value
    // source. limitsLastResolvedAt/limitsResolvedSummary are deliberately
    // NOT sent back — server-computed/read-only (see base-rule.dto.ts and
    // rule.service.ts, which strip them from the write path even if sent).
    limitSources: rule.limitSources ?? undefined,

    // Pro feature B (docs/shopify-pro-features-spec.md §B) — stock-aware maximum.
    stockCap: rule.stockCap ?? undefined,

    // Rule precedence — the override set the merchant accepted in the save
    // review (pages/rules/Rule.js). Null clears it. scopeProductsCount /
    // scopeVariantsCount are read-only and deliberately not sent.
    coverageAckHash: rule.coverageAckHash ?? null,

    hasPurchaseHistoryCondition: Boolean(rule.hasPurchaseHistoryCondition),
    purchaseHistory: rule.purchaseHistory ?? [],

    products: mapProducts(rule.scopeProducts),
    collections: mapCollections(rule.scopeCollections),
    tags: mapTags(rule.scopeTags),
    vendors: mapVendors(rule.scopeVendors),
    productTypes: mapProductTypes(rule.scopeProductTypes),
    priceRanges: mapPriceRanges(rule.scopePriceRanges),
    variantMetafieldConditions: mapVariantMetafieldConditions(rule.scopeVariantMetafieldConditions),

    customers: mapCustomers(rule.customers),
    customerTags: mapCustomerTags(rule.customerTags),
    customerSegments: mapCustomerSegments(rule.customerSegments),
    countries: mapCountries(rule.countries),
  };
}
